import React, { useState } from 'react';
import { OnboardingWrapper } from 'atoms/OnboardingWrapper/OnboardingWrapper';
import { NextPage } from 'next';
import { Text } from 'atoms/Text/Text';
import { defaultTheme } from 'constants/theme/defaultTheme';
import { Checkboxes } from 'atoms/Checkboxes/Checkboxes';
import { useQuery } from '@apollo/client';
import { getClinicalAttributes, GetClinicalAttributesType } from 'queries/clinicalAttributes';
import { formatForDropdown } from 'utils/formatForDropdown/formatForDropdown';
import { ClinicalAttributeWithAOS } from 'models/global';
import { Styles } from './styles';
import { Button } from 'atoms/Button/Button';
import { useRouter } from 'next/router';
import { useOnboarding } from 'contexts/OnboardingProvider/OnboardingProvider';
import { PATHS } from 'constants/paths';
import { Error } from 'atoms/Error';
import { Spacer } from 'components/Spacer/Spacer';
import { breakpoints } from 'stylesheet';
import { trackUserAttribute } from 'services/analytics/helpers';
import { UserAttributeType } from 'services/analytics/events';
import { getLabelsFromIds } from 'utils/clinicalAreaHelpers/clinicalAreaHelpers';
import { TEST_IDS } from 'constants/testIds';

export const ProductGroups: NextPage = () => {
  const [error, setError] = useState('');

  const { updateUser, user } = useOnboarding();
  const { query, push, replace } = useRouter();

  if (!query.aosId) {
    replace(PATHS.ONBOARDING_AOS);
  }

  const { data } = useQuery<GetClinicalAttributesType>(getClinicalAttributes, {
    onCompleted: data => {
      const aops = data.getAreasOfPractice?.filter(
        (i: ClinicalAttributeWithAOS) => i.areaOfSpecialisationId === query?.aosId,
      );
      if (!user.areasOfPracticeIds || user.areasOfPracticeIds.length === 0) {
        updateUser('areasOfPracticeIds')(aops.map((i: ClinicalAttributeWithAOS) => i.id));
      } else {
        updateUser('areasOfPracticeIds')(user.areasOfPracticeIds ?? []);
      }
    },
  });

  const aosName = React.useMemo(
    () => data?.getAreasOfSpecialisation.find(aos => aos.id === query.aosId)?.name.en,
    [data],
  );

  const handleChange = (value: string) => {
    if (user.areasOfPracticeIds?.includes(value)) {
      const updatedAops = user.areasOfPracticeIds.filter(i => i !== value);
      return updateUser('areasOfPracticeIds')(updatedAops);
    }

    updateUser('areasOfPracticeIds')([...(user.areasOfPracticeIds as string[]), value]);
  };

  const onClick = async () => {
    if (user.areasOfPracticeIds && user.areasOfPracticeIds.length > 0) {
      const value = getLabelsFromIds(data?.getAreasOfPractice, user.areasOfPracticeIds).join(', ');
      trackUserAttribute({
        userAttribute: UserAttributeType.AreasOfPractice,
        value,
      });
      push(PATHS.ONBOARDING_CLINICAL_AREA_DETAILS);
      return;
    }

    setError('Please select at least one product group');
  };

  if (!data || !user.areasOfPracticeIds) {
    return null;
  }

  return (
    <OnboardingWrapper currentStep={3}>
      <Styles.Content>
        <Text.h1 center data-testid={TEST_IDS.onboarding.productGroups.title}>
          What type of {aosName} products do you use?
        </Text.h1>
        <Text.body1 color={defaultTheme.colors.darkGrey} center>
          Deselect products, that you are not interested in
        </Text.body1>
        <Spacer.Column numberOfSpaces={10} />
        <Checkboxes
          options={formatForDropdown(
            data.getAreasOfPractice.filter(
              (i: ClinicalAttributeWithAOS) => i.areaOfSpecialisationId === query?.aosId,
            ),
          )}
          initialColumns={3}
          selected={user.areasOfPracticeIds}
          handleChange={handleChange}
          inline
          maxWidth={breakpoints.medium}
        />
        <Styles.ButtonWrapper>
          {error && (
            <Error noneAbsolute center marginBottom="8">
              {error}
            </Error>
          )}
          <Button
            text="Next Step"
            onClick={onClick}
            isOnboarding
            disabled={!user.areasOfPracticeIds.length}
            data-testid={TEST_IDS.onboarding.productGroups.submit}
          />
        </Styles.ButtonWrapper>
      </Styles.Content>
    </OnboardingWrapper>
  );
};
